import { createSelector, createFeatureSelector, select } from '@ngrx/store';

import { safeSelect, StateSelector } from '@fcom/core/selectors/selector-utils';
import { LocationsState } from '@fcom/locations/interfaces/locations-state.interface';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { mapValues } from '@fcom/core/utils/utils';
import { GlobalBookingTravelClass, GlobalBookingTravelClassValue, LocalDate } from '@fcom/core';
import { LocationsMap, LocationRouteType } from '@fcom/core-api/interfaces/location-routecff.interface';
import { PaxAmount } from '@fcom/dapi/interfaces/pax-details.interface';

import {
  CommonState,
  CommonFeatureState,
  GlobalBookingState,
  TravelClassState,
  TravelClasses,
  GlobalBookingFlight,
  LocationPair,
} from '../store.interface';
import { COMMON_FEATURE_KEY } from '../constants';

const convertToLocalDateIfNeeded = (date: LocalDate | string): LocalDate =>
  date ? (date instanceof LocalDate ? date : new LocalDate(date)) : undefined;

const mapToCachedLocations = (flights: GlobalBookingFlight[], locationsMap: LocationsMap): GlobalBookingFlight[] =>
  flights?.map(({ origin, destination, departureDate }) => ({
    origin: locationsMap?.[origin?.locationCode] ?? origin,
    destination: locationsMap?.[destination?.locationCode] ?? destination,
    departureDate: convertToLocalDateIfNeeded(departureDate),
  }));

const _cachedLocationsMap = createSelector(
  (appState: BookingAppState) => appState?.locations,
  (locationsState: LocationsState) => locationsState?.locations
);

const _selectCommonState = createFeatureSelector<CommonFeatureState, CommonState>(COMMON_FEATURE_KEY);

export const selectGlobalBookingState = createSelector(
  _selectCommonState,
  _cachedLocationsMap,
  (state: CommonState, cachedLocations: LocationsMap) => ({
    ...state.globalBooking,
    flights: mapToCachedLocations(state.globalBooking.flights, cachedLocations),
  })
);
export const selectGlobalBookingTripType = createSelector(
  selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.tripType
);
export const selectGlobalBookingFlights = createSelector(selectGlobalBookingState, (globalBookingState) =>
  globalBookingState.flights.map((flight) => ({
    ...flight,
    departureDate: convertToLocalDateIfNeeded(flight.departureDate),
  }))
);

const _getGlobalBookingFlights = selectGlobalBookingFlights;

const _globalBookingTravelClassState = createSelector(
  selectGlobalBookingState,
  (state: GlobalBookingState) => state.travelClasses
);

export const selectGlobalBookingTravelDates = createSelector(
  _getGlobalBookingFlights,
  (flights: GlobalBookingFlight[]) => ({
    departureDate: convertToLocalDateIfNeeded(flights[0]?.departureDate),
    ...(flights[1]?.departureDate && {
      returnDate: convertToLocalDateIfNeeded(flights[1]?.departureDate),
    }),
  })
);

export const selectGlobalBookingAvailableTravelClasses = createSelector(
  _globalBookingTravelClassState,
  (travelClasses: TravelClassState): GlobalBookingTravelClass[] =>
    Object.keys(travelClasses.availability).reduce((availableClasses, travelClass: GlobalBookingTravelClass) => {
      if (travelClasses.availability[travelClass]) {
        availableClasses.push(travelClass);
      }
      return availableClasses;
    }, [])
);

export const selectGlobalBookingTravelClass = createSelector(
  selectGlobalBookingState,
  selectGlobalBookingAvailableTravelClasses,
  (globalBookingState: GlobalBookingState, availableClasses: GlobalBookingTravelClass[]) =>
    availableClasses.includes(globalBookingState.travelClasses.selected)
      ? globalBookingState.travelClasses.selected
      : availableClasses[0]
);

const _globalBookingTravelClasses = createSelector(
  _globalBookingTravelClassState,
  (tcs: TravelClassState): TravelClasses =>
    tcs
      ? mapValues(tcs.availability, (available: boolean, key: GlobalBookingTravelClassValue) => ({
          available,
          selected: tcs.selected === key,
        }))
      : undefined
);
const _getGlobalBookingOrigin = createSelector(
  selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.origin
);

const _getGlobalBookingRouteType = createSelector(
  selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.routeType
);

export const selectGlobalBookingDiscountCode = createSelector(
  selectGlobalBookingState,
  (globalBookingState: GlobalBookingState) => globalBookingState.discountCode
);

export const selectGlobalBookingLocations = createSelector(_getGlobalBookingFlights, (flights: GlobalBookingFlight[]) =>
  flights.map(({ origin, destination }) => ({ origin, destination }))
);

const _globalBookingLocations = selectGlobalBookingLocations;

export const selectGlobalBookingPaxAmount = createSelector(
  selectGlobalBookingState,
  (globalBookingState) => globalBookingState?.paxAmount
);
export const globalBookingTravelClasses = (): StateSelector<CommonFeatureState, TravelClasses> =>
  select(_globalBookingTravelClasses);
export const globalBookingOrigin = (): StateSelector<CommonFeatureState, string> => select(_getGlobalBookingOrigin);
export const globalBookingRouteType = (): StateSelector<CommonFeatureState, LocationRouteType> =>
  select(_getGlobalBookingRouteType);
export const globalBookingLocations = (): StateSelector<CommonFeatureState, LocationPair[]> =>
  select(_globalBookingLocations);
export const globalBookingPaxAmount = (): StateSelector<CommonFeatureState, PaxAmount> =>
  safeSelect(selectGlobalBookingPaxAmount);
export const globalBookingFlights = (): StateSelector<CommonFeatureState, GlobalBookingFlight[]> =>
  safeSelect(_getGlobalBookingFlights);
